/**
 * PlaylistsDrawer - Live-page slide-over for public playlists, "My Vibes",
 * the user's own playlists (create/edit/delete), and followed playlists.
 * Playing a playlist loads it into the global audio player.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Play, Plus, ListMusic, Globe, Lock, Sparkles, Heart,
  Edit2, Trash2, ChevronLeft, Music, Users, Flame
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useAudioPlayer } from '../context/AudioPlayerContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const TABS = [
  { id: 'discover', label: 'Discover', icon: Globe },
  { id: 'vibes', label: 'My Vibes', icon: Sparkles },
  { id: 'mine', label: 'Mine', icon: ListMusic },
  { id: 'following', label: 'Following', icon: Heart },
];

const Cover = ({ playlist, className = '' }) => (
  <div className={`relative rounded-xl flex items-center justify-center overflow-hidden ${playlist.is_hot ? 'bg-gradient-to-br from-[#FF6A00]/40 to-[#FF007F]/30' : 'bg-gradient-to-br from-[#FF007F]/25 to-[#00F0FF]/25'} ${className}`}>
    {playlist.cover_image ? (
      <img src={playlist.cover_image} alt={playlist.name} className="w-full h-full object-cover" />
    ) : playlist.is_hot ? (
      <Flame className="w-7 h-7 text-[#FF6A00]" />
    ) : playlist.is_my_vibes ? (
      <Sparkles className="w-7 h-7 text-[#00F0FF]" />
    ) : (
      <ListMusic className="w-7 h-7 text-white/70" />
    )}
  </div>
);

const PlaylistRow = ({ playlist, canEdit, onPlay, onOpen, onFollow, onEdit, onDelete }) => (
  <div
    className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-white/5 transition-colors group"
    data-testid={`playlist-row-${playlist.id}`}
  >
    <button onClick={() => onOpen(playlist)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
      <Cover playlist={playlist} className="w-14 h-14 shrink-0" />
      <div className="min-w-0">
        <p className="text-white font-semibold truncate flex items-center gap-1.5">
          {playlist.name}
          {playlist.is_public ? null : <Lock className="w-3 h-3 text-white/40" />}
        </p>
        <p className="text-[#71717A] text-xs truncate">
          {playlist.track_count || 0} tracks
          {typeof playlist.follower_count === 'number' && (
            <span className="inline-flex items-center gap-1 ml-2">
              <Users className="w-3 h-3" />{playlist.follower_count}
            </span>
          )}
        </p>
      </div>
    </button>

    <button
      onClick={() => onPlay(playlist)}
      className="w-10 h-10 rounded-full bg-[#00F0FF] flex items-center justify-center shrink-0 hover:scale-105 active:scale-95 transition-transform"
      data-testid={`playlist-play-${playlist.id}`}
      title="Play"
    >
      <Play className="w-5 h-5 text-[#050505] ml-0.5" />
    </button>

    {onFollow && (
      <button
        onClick={() => onFollow(playlist)}
        className={`px-3 py-2 rounded-full text-xs font-semibold shrink-0 transition-colors ${
          playlist.is_following ? 'bg-white/10 text-white' : 'bg-[#FF007F] text-white'
        }`}
        data-testid={`playlist-follow-${playlist.id}`}
      >
        {playlist.is_following ? 'Following' : 'Follow'}
      </button>
    )}

    {canEdit && (
      <div className="flex gap-1 shrink-0">
        <button
          onClick={() => onEdit(playlist)}
          className="p-2 rounded-lg bg-white/5 hover:bg-white/15 text-white/80 transition-colors"
          data-testid={`playlist-edit-${playlist.id}`}
        >
          <Edit2 className="w-4 h-4" />
        </button>
        <button
          onClick={() => onDelete(playlist)}
          className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/25 text-red-400 transition-colors"
          data-testid={`playlist-delete-${playlist.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    )}
  </div>
);

const EditModal = ({ visible, editing, onClose, onSaved }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(editing?.name || '');
    setDescription(editing?.description || '');
    setIsPublic(editing?.is_public ?? true);
  }, [editing, visible]);

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const body = { name: name.trim(), description: description.trim(), is_public: isPublic };
      if (editing) {
        await axios.put(`${API_URL}/api/artists/playlists/${editing.id}`, body, { headers: authHeaders() });
        toast.success('Playlist updated');
      } else {
        await axios.post(`${API_URL}/api/artists/playlists`, body, { headers: authHeaders() });
        toast.success('Playlist created');
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save playlist');
    } finally {
      setSaving(false);
    }
  };

  if (!visible) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ scale: 0.92, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="relative w-full max-w-md bg-[#12101A] rounded-2xl p-6 border border-white/10"
      >
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10 text-white/60">
          <X className="w-5 h-5" />
        </button>
        <h2 className="text-lg font-bold text-white mb-5" style={{ fontFamily: 'Unbounded' }}>
          {editing ? 'Edit Playlist' : 'New Playlist'}
        </h2>
        <div className="space-y-4">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Playlist name"
            className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#52525B] focus:outline-none focus:border-[#00F0FF]"
            data-testid="drawer-playlist-name-input"
          />
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Description (optional)"
            rows={2}
            className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#52525B] focus:outline-none focus:border-[#00F0FF] resize-none"
          />
          <div className="flex items-center justify-between p-3 rounded-xl bg-white/5">
            <div className="flex items-center gap-3">
              {isPublic ? <Globe className="w-5 h-5 text-[#00F0FF]" /> : <Lock className="w-5 h-5 text-[#FF007F]" />}
              <div>
                <p className="text-white text-sm font-medium">{isPublic ? 'Public' : 'Private'}</p>
                <p className="text-[#71717A] text-xs">{isPublic ? 'Shows on the Live page' : 'Only you can see'}</p>
              </div>
            </div>
            <button
              onClick={() => setIsPublic(!isPublic)}
              className={`w-12 h-6 rounded-full transition-colors ${isPublic ? 'bg-[#00F0FF]' : 'bg-white/20'}`}
              data-testid="drawer-playlist-public-toggle"
            >
              <div className={`w-5 h-5 rounded-full bg-white transition-transform ${isPublic ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
          </div>
        </div>
        <button
          onClick={save}
          disabled={!name.trim() || saving}
          className="w-full mt-6 py-3.5 rounded-xl bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white font-semibold disabled:opacity-50"
          data-testid="drawer-save-playlist-btn"
        >
          {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create'}
        </button>
      </motion.div>
    </div>
  );
};

const PlaylistsDrawer = ({ open, onClose }) => {
  const { user, isAuthenticated } = useAuth();
  const { playTrack } = useAudioPlayer();
  const isOwner = !!(user?.isSuperAdmin || user?.role === 'super_admin');

  const [tab, setTab] = useState('discover');
  const [lists, setLists] = useState({ discover: [], vibes: [], mine: [], following: [] });
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null); // {playlist, tracks}
  const [showEdit, setShowEdit] = useState(false);
  const [editing, setEditing] = useState(null);

  const canEditPlaylist = useCallback(
    (pl) => !pl.is_my_vibes && !pl.is_hot && (isOwner || pl.user_id === user?.id),
    [isOwner, user]
  );

  const fetchTab = useCallback(async (which) => {
    setLoading(true);
    try {
      const map = {
        discover: '/api/artists/playlists/public',
        vibes: '/api/artists/playlists/my-vibes',
        mine: '/api/artists/playlists',
        following: '/api/artists/playlists/following',
      };
      const res = await axios.get(`${API_URL}${map[which]}`, { headers: authHeaders() });
      if (which === 'vibes') {
        const pl = res.data.playlist;
        setLists((p) => ({ ...p, vibes: pl ? [{ ...pl, _tracks: res.data.tracks }] : [] }));
      } else {
        setLists((p) => ({ ...p, [which]: res.data.playlists || [] }));
      }
    } catch (e) {
      console.error('playlist fetch error', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchTab(tab);
  }, [open, tab, fetchTab]);

  const openDetail = async (pl) => {
    if (pl.is_my_vibes && pl._tracks) {
      setDetail({ playlist: pl, tracks: pl._tracks });
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/api/artists/playlists/${pl.id}`, { headers: authHeaders() });
      setDetail({ playlist: { ...pl, ...res.data.playlist }, tracks: res.data.tracks || [] });
    } catch (e) {
      toast.error('Could not open playlist');
    }
  };

  const playPlaylist = async (pl) => {
    let tracks = pl._tracks;
    if (!tracks) {
      try {
        const res = await axios.get(`${API_URL}/api/artists/playlists/${pl.id}`, { headers: authHeaders() });
        tracks = res.data.tracks || [];
      } catch { tracks = []; }
    }
    const playable = (tracks || []).filter((t) => t.audio_url);
    if (!playable.length) {
      toast.error('No playable tracks in this playlist yet');
      return;
    }
    playTrack(playable[0], playable, 0);
    toast.success(`Playing "${pl.name}"`);
    onClose();
  };

  const toggleFollow = async (pl) => {
    try {
      if (pl.is_following) {
        await axios.delete(`${API_URL}/api/artists/playlists/${pl.id}/follow`, { headers: authHeaders() });
      } else {
        await axios.post(`${API_URL}/api/artists/playlists/${pl.id}/follow`, {}, { headers: authHeaders() });
      }
      // optimistic update across visible lists
      setLists((prev) => {
        const upd = (arr) => arr.map((x) => x.id === pl.id
          ? { ...x, is_following: !pl.is_following, follower_count: (x.follower_count || 0) + (pl.is_following ? -1 : 1) }
          : x);
        return { ...prev, discover: upd(prev.discover), following: upd(prev.following) };
      });
      if (tab === 'following') fetchTab('following');
    } catch (e) {
      toast.error('Could not update follow');
    }
  };

  const deletePlaylist = async (pl) => {
    if (!window.confirm(`Delete "${pl.name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/api/artists/playlists/${pl.id}`, { headers: authHeaders() });
      toast.success('Playlist deleted');
      fetchTab(tab);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete');
    }
  };

  const removeTrack = async (trackId) => {
    if (!detail) return;
    try {
      await axios.delete(`${API_URL}/api/artists/playlists/${detail.playlist.id}/tracks/${trackId}`, { headers: authHeaders() });
      setDetail((d) => ({ ...d, tracks: d.tracks.filter((t) => t.id !== trackId) }));
    } catch (e) {
      toast.error('Could not remove track');
    }
  };

  const currentList = lists[tab] || [];

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 320, damping: 34 }}
            className="fixed right-0 top-0 bottom-0 z-[61] w-full max-w-md bg-[#0B0A10] border-l border-white/10 flex flex-col"
            data-testid="playlists-drawer"
          >
            {/* Header */}
            <div className="p-4 border-b border-white/10">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Unbounded' }}>
                  <ListMusic className="w-5 h-5 text-[#00F0FF]" /> Playlists
                </h2>
                <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10 text-white/70" data-testid="close-playlists-drawer">
                  <X className="w-5 h-5" />
                </button>
              </div>
              {!detail && (
                <div className="flex gap-1 bg-white/5 rounded-xl p-1">
                  {TABS.filter((t) => isAuthenticated || t.id === 'discover').map((t) => {
                    const Icon = t.icon;
                    return (
                      <button
                        key={t.id}
                        onClick={() => setTab(t.id)}
                        className={`flex-1 flex items-center justify-center gap-1 py-2 px-1 rounded-lg text-xs font-medium transition-colors whitespace-nowrap ${
                          tab === t.id ? 'bg-white/15 text-white' : 'text-white/50 hover:text-white'
                        }`}
                        data-testid={`playlists-tab-${t.id}`}
                        title={t.label}
                      >
                        <Icon className="w-3.5 h-3.5 shrink-0" /> <span className="hidden sm:inline">{t.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-3">
              {detail ? (
                <div>
                  <button
                    onClick={() => setDetail(null)}
                    className="flex items-center gap-1 text-white/60 hover:text-white text-sm mb-3"
                    data-testid="playlist-detail-back"
                  >
                    <ChevronLeft className="w-4 h-4" /> Back
                  </button>
                  <div className="flex items-center gap-4 mb-4">
                    <Cover playlist={detail.playlist} className="w-20 h-20 shrink-0" />
                    <div className="min-w-0">
                      <h3 className="text-white font-bold text-lg truncate">{detail.playlist.name}</h3>
                      <p className="text-[#71717A] text-xs">{detail.tracks.length} tracks · by {detail.playlist.user_name || 'NLT'}</p>
                      <button
                        onClick={() => playPlaylist({ ...detail.playlist, _tracks: detail.tracks })}
                        className="mt-2 px-4 py-2 rounded-full bg-[#00F0FF] text-[#050505] text-sm font-semibold flex items-center gap-1"
                        data-testid="playlist-detail-play"
                      >
                        <Play className="w-4 h-4" /> Play
                      </button>
                    </div>
                  </div>
                  <div className="space-y-1">
                    {detail.tracks.map((t, i) => (
                      <div key={t.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5">
                        <span className="text-white/30 text-xs w-5 text-center">{i + 1}</span>
                        {t.artwork ? (
                          <img src={t.artwork} alt="" className="w-10 h-10 rounded object-cover" />
                        ) : (
                          <div className="w-10 h-10 rounded bg-white/10 flex items-center justify-center"><Music className="w-4 h-4 text-white/40" /></div>
                        )}
                        <button onClick={() => playTrack(t, detail.tracks.filter((x) => x.audio_url), i)} className="flex-1 min-w-0 text-left">
                          <p className="text-white text-sm truncate">{t.track_title || 'Untitled'}</p>
                          <p className="text-[#71717A] text-xs truncate">{t.artist_name || ''}</p>
                        </button>
                        {canEditPlaylist(detail.playlist) && (
                          <button onClick={() => removeTrack(t.id)} className="p-1.5 rounded text-red-400 hover:bg-red-500/15" data-testid={`remove-track-${t.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                    {detail.tracks.length === 0 && (
                      <p className="text-center text-white/40 text-sm py-8">No tracks yet.</p>
                    )}
                  </div>
                </div>
              ) : loading ? (
                <div className="flex justify-center py-16">
                  <div className="w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : currentList.length === 0 ? (
                <div className="text-center py-16 px-6">
                  <ListMusic className="w-14 h-14 mx-auto mb-3 text-[#52525B]" />
                  <p className="text-white font-semibold mb-1">
                    {tab === 'discover' && 'No public playlists yet'}
                    {tab === 'vibes' && 'Your Vibes are empty'}
                    {tab === 'mine' && 'No playlists yet'}
                    {tab === 'following' && "You're not following any playlists"}
                  </p>
                  <p className="text-[#71717A] text-sm">
                    {tab === 'vibes'
                      ? 'Vote "Issa Hit 🔥" or "Rocking" on songs and they land here automatically.'
                      : tab === 'discover'
                      ? 'Public playlists curated by artists & NLT will show up here.'
                      : 'Create a playlist to get started.'}
                  </p>
                </div>
              ) : (
                <div className="space-y-1">
                  {currentList.map((pl) => (
                    <PlaylistRow
                      key={pl.id}
                      playlist={pl}
                      canEdit={canEditPlaylist(pl)}
                      onPlay={playPlaylist}
                      onOpen={openDetail}
                      onFollow={tab === 'discover' && !canEditPlaylist(pl) ? toggleFollow : null}
                      onEdit={(p) => { setEditing(p); setShowEdit(true); }}
                      onDelete={deletePlaylist}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Footer: create */}
            {isAuthenticated && !detail && (tab === 'mine' || (isOwner && tab === 'discover')) && (
              <div className="p-3 border-t border-white/10">
                <button
                  onClick={() => { setEditing(null); setShowEdit(true); }}
                  className="w-full py-3 rounded-xl bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white font-semibold flex items-center justify-center gap-2"
                  data-testid="drawer-create-playlist-btn"
                >
                  <Plus className="w-5 h-5" /> New Playlist
                </button>
              </div>
            )}
          </motion.div>

          <EditModal
            visible={showEdit}
            editing={editing}
            onClose={() => setShowEdit(false)}
            onSaved={() => { fetchTab(tab); if (tab !== 'mine') fetchTab('mine'); }}
          />
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default PlaylistsDrawer;
